/* eslint-disable max-lines-per-function */
import {
  Button,
  buttonVariants,
  Field,
  FieldError,
  FieldLabel,
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  Skeleton,
} from '@e2n/ui';
import { Beruf } from '@/domain/mitarbeiter/features/rotationsplan/entities/Beruf';
import { Mitarbeiter } from '@/domain/mitarbeiter/features/rotationsplan/entities/Mitarbeiter';
import { MitarbeiterSelect } from '@/domain/mitarbeiter/features/rotationsplan/features/CreateRotationsplan/components/MitarbeiterSelect';
import { useStandortContext } from '@/features/standortContext/StandortContext';
import { toast } from '@/hooks/use-toast';
import { cn } from '@/lib/utils';
import { faArrowLeft } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useForm } from '@tanstack/react-form';
import { Suspense, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import z from 'zod';
import * as Sentry from '@sentry/react';
import { BerufeSelect } from '@/domain/mitarbeiter/features/rotationsplan/features/CreateRotationsplan/components/BerufeSelect';
import { useCreateRotationsplan } from '@/domain/mitarbeiter/features/rotationsplan/hooks/useCreateRotationsplan/useCreateRotationsplan';
import { checkIfMitarbeiterHasRotationsplan } from '@/domain/mitarbeiter/features/rotationsplan/features/CreateRotationsplan/checkIfMitarbeiterHasRotationsplan';
import { useRotationsplaene } from '@/domain/mitarbeiter/features/rotationsplan/hooks/useRotationsplaene/useRotationsplaene';
import { ConfirmDialog } from '@/domain/mitarbeiter/features/rotationsplan/features/CreateRotationsplan/ConfirmDialog';

export function CreateRotationsplan() {
  const { t } = useTranslation();
  const [createRotationsplan] = useCreateRotationsplan();
  const { currentStandort, standorte } = useStandortContext();
  const navigate = useNavigate();
  const standortIds = standorte.map((standort) => standort.id);
  const { rotationsplaene } = useRotationsplaene(standortIds);
  const [rotationsplanExistiertBereits, setRotationsplanExistiertBereits] =
    useState(false);
  const [isOpen, setIsOpen] = useState(false);

  const schema = z.object({
    beruf: z.instanceof(Beruf, {
      message: t('generalValidationError.required'),
    }),
    mitarbeiter: z.instanceof(Mitarbeiter, {
      message: t('generalValidationError.required'),
    }),
  });

  const form = useForm({
    defaultValues: {
      beruf: undefined,
      mitarbeiter: undefined,
    },
    validators: {
      onChange: schema,
    },
    onSubmit: async ({
      value,
    }: {
      value: { beruf: Beruf; mitarbeiter: Mitarbeiter };
    }) => {
      createRotationsplan({
        input: {
          berufId: value.beruf.id,
          mitarbeiterId: value.mitarbeiter.id,
          standortId: currentStandort.id,
        },
      })
        .then((result) => {
          navigate(
            `/mitarbeiter/rotationsplan/${result.data.mitarbeiter_createRotationsplan.id}`,
          );
          toast({
            title: t(
              'domain.mitarbeiter.rotationsplan.rotationsplaene.createRotationsplan.success',
            ),
          });
          setRotationsplanExistiertBereits(false);
          setIsOpen(false);
        })
        .catch((error) => {
          Sentry.captureException(error);
          toast({
            title: t(
              'domain.mitarbeiter.rotationsplan.rotationsplaene.createRotationsplan.error',
            ),
          });
        });
    },
  });

  return (
    <>
      <Sheet
        open
        onOpenChange={(open) => {
          if (!open) {
            form.reset();
            navigate('/mitarbeiter/rotationsplaene/uebersicht', {
              replace: true,
            });
          }
        }}>
        <SheetContent
          removeCloseIcon
          className="flex flex-col gap-6 w-full md:min-w-120 p-0 h-full">
          <SheetHeader className="space-y-0 flex flex-row items-center justify-between bg-neutrals-200 px-4 py-2">
            <div className="flex items-center gap-2">
              <Link
                to={`/mitarbeiter/rotationsplaene/uebersicht`}
                className={cn(
                  buttonVariants({ variant: 'ghost', size: 'icon' }),
                )}>
                <FontAwesomeIcon
                  className="text-primary-blue-600"
                  icon={faArrowLeft}
                />
              </Link>
              <SheetTitle className="text-sm text-neutrals-800">
                {t(
                  'domain.mitarbeiter.rotationsplan.rotationsplaene.createRotationsplan.titel',
                )}
              </SheetTitle>
            </div>
            {rotationsplanExistiertBereits ? (
              <Button
                disabled={form.state.isSubmitting}
                variant="ghost-primary"
                className="flex md:hidden"
                onClick={() => {
                  form.validateAllFields('change');

                  if (form.state.isValid) {
                    setIsOpen(true);
                  }
                }}>
                {t('actions.fertig')}
              </Button>
            ) : (
              <Button
                className="flex md:hidden"
                variant="ghost-primary"
                disabled={!form.state.isValid || form.state.isSubmitting}
                form="createRotationsplanForm">
                {t('actions.fertig')}
              </Button>
            )}
          </SheetHeader>
          <div className="flex flex-col gap-4 px-6 grow">
            <div className="text-xs text-neutrals-700">
              {t(
                'domain.mitarbeiter.rotationsplan.rotationsplaene.hinweisPflichtfelder',
              )}
            </div>
            <form
              id="createRotationsplanForm"
              className="flex flex-col gap-4 h-full"
              onSubmit={(e) => {
                e.preventDefault();
                form.handleSubmit();
              }}>
              <form.Field
                name="mitarbeiter"
                listeners={{
                  onChange: ({ value }) => {
                    if (!value) {
                      setRotationsplanExistiertBereits(false);
                      return;
                    }

                    const rotationsplanExistiert =
                      checkIfMitarbeiterHasRotationsplan(
                        value.id,
                        rotationsplaene,
                      );
                    setRotationsplanExistiertBereits(rotationsplanExistiert);
                  },
                }}>
                {(field) => {
                  const isInvalid =
                    field.state.meta.isTouched && !field.state.meta.isValid;

                  return (
                    <Field
                      data-invalid={isInvalid}
                      className="flex flex-col gap-1 w-full">
                      <FieldLabel htmlFor={field.name} aria-invalid={isInvalid}>
                        {t(
                          'domain.mitarbeiter.rotationsplan.rotationsplaene.createRotationsplan.fields.mitarbeiter.label',
                        ) + '*'}
                      </FieldLabel>
                      <Suspense fallback={<Skeleton className="w-108 h-10" />}>
                        <MitarbeiterSelect onChange={field.handleChange} />
                      </Suspense>
                      {isInvalid && (
                        <FieldError errors={field.state.meta.errors} />
                      )}
                      {rotationsplanExistiertBereits && (
                        <FieldError
                          variant="warning"
                          errors={[
                            {
                              message: t(
                                'domain.mitarbeiter.rotationsplan.rotationsplaene.createRotationsplan.planExistiert.validationMessage',
                              ),
                            },
                          ]}
                        />
                      )}
                    </Field>
                  );
                }}
              </form.Field>
              <form.Field name="beruf">
                {(field) => {
                  const isInvalid =
                    field.state.meta.isTouched && !field.state.meta.isValid;

                  return (
                    <Field
                      data-invalid={isInvalid}
                      className="flex flex-col gap-1">
                      <FieldLabel htmlFor={field.name} aria-invalid={isInvalid}>
                        {t(
                          'domain.mitarbeiter.rotationsplan.rotationsplaene.createRotationsplan.fields.beruf.label',
                        ) + '*'}
                      </FieldLabel>
                      <BerufeSelect
                        handleChange={field.handleChange}
                        value={field.state.value}
                      />
                      {isInvalid && (
                        <FieldError errors={field.state.meta.errors} />
                      )}
                    </Field>
                  );
                }}
              </form.Field>
            </form>
          </div>
          <div className="h-px w-full bg-neutrals-300" />
          <div className="hidden md:flex justify-end px-8 pb-4">
            {rotationsplanExistiertBereits ? (
              <Button
                onClick={() => {
                  form.validateAllFields('change');

                  if (form.state.isValid) {
                    setIsOpen(true);
                  }
                }}
                disabled={form.state.isSubmitting}>
                {t('actions.fertig')}
              </Button>
            ) : (
              <Button
                disabled={!form.state.isValid || form.state.isSubmitting}
                form="createRotationsplanForm">
                {t('actions.fertig')}
              </Button>
            )}
          </div>
          {isOpen && rotationsplanExistiertBereits && (
            <ConfirmDialog
              isOpen={isOpen}
              setIsOpen={setIsOpen}
              isSubmitting={form.state.isSubmitting}
            />
          )}
        </SheetContent>
      </Sheet>
    </>
  );
}
